import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { mergeMap, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Product, ProductVariant } from './products.service';
import { Tenant } from './tenants.service';
import { AuthService } from './auth.service';
import { DesktopInvoice, DesktopInvoiceDetails } from './invoices.service';

export type DesktopLicense = {
  id: string;
  tenant_id: string;
  product_variant_id: string;
  parent_desktop_license_id?: string;
  machine_id?: string;
  start_date?: string;
  expiration?: string;
  transfers_left: number;
  site_license: boolean;
  renewal: boolean;
  active: boolean;
  new_license: boolean;
  will_renew: boolean;
  created_at: string;
  notes?: string;
  unique_id?: string;
};

export type DesktopLicenseWithProduct = DesktopLicense & {
  product: Product;
  product_variant: ProductVariant;
};

export type DesktopLicenseDetails = DesktopLicenseWithProduct & {
  close_to_expiration: boolean;
  invoices?: DesktopInvoice[];
  activation_key?: ActivationKey;
};

export type LicenseCreationIntent = 'new_primary' | 'add_ons';

export type NewDesktopLicenseDetails = {
  product_variant_id: string;
  start_date: Date;
  expiration: Date;
  new_license: boolean;
  is_site_license: boolean;
  parent_desktop_license_id?: string;
};

export type CreateDesktopLicenseRequest = {
  tenant_id: string;
  creation_intent: LicenseCreationIntent;
  new_primary_license?: NewDesktopLicenseDetails;
  add_ons_parent_license_id?: string;
  add_on_licenses?: NewDesktopLicenseDetails[];
  skip_parent_license_check: boolean;
};

export type CreateDesktopLicenseResponse = {
  new_primary_license?: DesktopLicenseWithProduct;
  add_on_licenses?: DesktopLicenseWithProduct[];
};

export type LicensesResponse = {
  desktop_licenses: CategorizedLicenses;
};

export type CategorizedLicenses = {
  active: DesktopLicenseDetails[];
  expired: DesktopLicenseDetails[];
  pending: DesktopLicenseDetails[];
  disabled: DesktopLicenseDetails[];
};

export type LicenseResponse = {
  desktop_license: DesktopLicense;
};

type LicenseWithProductResponse = {
  desktop_license: DesktopLicenseWithProduct;
};


export type LicenseDetailsResponse = {
  desktop_license: DesktopLicenseWithProduct;
  tenant: Tenant;
};

export type QueryDesktopLicensesRequest = {
  product_ids: string[];
  from_date?: string;
  to_date?: string;
};

export type DesktopLicenseQueryTenant = {
  id: string;
  account_number: string;
  company: string;
};

export type DesktopLicenseQueryItem = DesktopLicenseWithProduct & {
  tenant: DesktopLicenseQueryTenant;
};

export type QueryDesktopLicensesResponse = {
  desktop_licenses: DesktopLicenseQueryItem[];
};

export type UpdateDesktopLicenseRequest = {
  active?: boolean;
  site_license?: boolean;
  renewal?: boolean;
  will_renew?: boolean;
  new_license?: boolean;
  expiration?: Date;
  start_date?: Date;
  transfers_left?: number;
  price_lock_level?: number;
  parent_desktop_license_id?: string;
  notes?: string;
  unique_id?: string;
};

export type UpdateDesktopLicenseWillRenewRequest = {
  desktop_license_id: string;
  will_renew: boolean;
};

export type UpdateDesktopLicenseWillRenewResponse = {
  desktop_licenses: DesktopLicense[];
  desktop_invoices?: DesktopInvoiceDetails[];
};

type PreemptiveExpirationRequest = {
  start_date: Date;
  product_variant_id: string;
  parent_license_id?: string;
};

type PreemptiveExpirationResponse = {
  expiration_date: string;
};

type DeleteDesktopLicenseResponse = {
  success: boolean;
};

export type LicenseDurationOptionsResponse = {
  product_variant: ProductVariant;
  new_expiration: string;
  cost: number;
  is_base: boolean;
  cost_savings?: LicenseCostSavings;
};

export type LicenseCostSavings = {
  total_price: number;
  per_year_price: number;
  base_total_price: number;
  percent_discount: number;
};

export type HasCloseToExpirationResponse = {
  has_close_to_expiration: boolean;
};

export type ActivationKey = {
  id: string;
  activation_key: string;
  generation_trigger?: string;
  product_code: string;
  expiration_date: string;
  user_id: string;
  tenant_id: string;
  product_variant_id: string;
  unique_id?: string;
  desktop_license_id: string;
  request_ip?: string;
  user_agent?: string;
  accept_language?: string;
  referer?: string;
  encrypted: boolean;
};

export type TransferLicenseResponse = ActivationKey;

export type TransferLicensesResponse = {
  activation_keys: ActivationKey[];
};

export type ApplyInitialUniqueIDsRequest = {
  tenant_id: string;
  generate_activation_keys: boolean;
  updates: { license_id: string; unique_id: string }[];
};

export type ApplyInitialUniqueIDsResponse = {
  activation_keys: ActivationKey[];
};

export type DesktopLicenseFieldChange = {
  field: string;
  old: unknown;
  new: unknown;
};

export type DesktopLicenseChangeLogUserSummary = {
  id: string;
  username: string;
  email: string;
};

export type DesktopLicenseChangeLogVariantSummary = {
  id: string;
  description: string;
  display_duration: string;
  price_lock_level: number;
  duration_months: number;
};

export type DesktopLicenseChangeLogEntry = {
  id: string;
  desktop_license_id: string;
  tenant_id: string;
  change_source: string;
  reason: string;
  created_at: string;
  changed_by_user?: DesktopLicenseChangeLogUserSummary;
  old_product_variant?: DesktopLicenseChangeLogVariantSummary;
  new_product_variant?: DesktopLicenseChangeLogVariantSummary;
  field_changes: DesktopLicenseFieldChange[];
};

export type DesktopLicenseChangeLogResponse = {
  change_logs: DesktopLicenseChangeLogEntry[];
};

@Injectable({
  providedIn: 'root',
})
export class LicensesService {
  private getLicenseUrl = environment.serverUrl + '/licenses/get';
  private getLicenseDetailsUrl =
    environment.serverUrl + '/licenses/get-details';
  private licensesByTentantUrl = environment.serverUrl + '/licenses/for-tenant';
  private hasCloseToExpirationLicensesUrl =
    environment.serverUrl + '/licenses/has-close-to-expiration';
  private addLicenseToTenantUrl = environment.serverUrl + '/licenses/create';
  private updateWillRenewUrl =
    environment.serverUrl + '/licenses/update-will-renew';
  private updateLicenseUrl = environment.serverUrl + '/licenses/update';
  private preemptiveExpirationUrl =
    environment.serverUrl +
    '/licenses/preemptive-license-expiration-calculation';
  private getDurationOptionsUrl =
    environment.serverUrl + '/licenses/get-duration-options';
  private getPriceLockLevelsUrl =
    environment.serverUrl + '/licenses/get-price-lock-levels';
  private deleteDesktopLicenseUrl = environment.serverUrl + '/licenses/delete';
  private transferLicenseUrl = environment.serverUrl + '/licenses/transfer';
  private applyInitialUniqueIdsUrl =
    environment.serverUrl + '/licenses/apply-initial-unique-ids';
  private getLicenseChangeLogUrl = environment.serverUrl + '/licenses/change-log';
  private queryLicensesUrl = environment.serverUrl + '/licenses/query';

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
    private authService: AuthService
  ) {}

  getLicensesForCurrentUser(includeKeys: boolean = false) {
    const user = this.authService.currentUser;
    if (!user) {
      throw new Error('currentUser not found on auth service');
    }
    return this.getLicensesForTenant(user.tenant_id, includeKeys);
  }

  getLicense(licenseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<LicenseResponse>(
          this.getLicenseUrl + '/' + licenseId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getLicenseDetails(licenseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<LicenseDetailsResponse>(
          this.getLicenseDetailsUrl + '/' + licenseId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }


  getLicenseChangeLog(licenseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesktopLicenseChangeLogResponse>(
          this.getLicenseChangeLogUrl + '/' + licenseId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getLicensesForTenant(tenantId: string, includeKeys: boolean = false) {
    let url = this.licensesByTentantUrl + '/' + tenantId;
    if (includeKeys) {
      url += '?include_keys=true';
    }
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<LicensesResponse>(url, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  queryDesktopLicenses(request: QueryDesktopLicensesRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<QueryDesktopLicensesResponse>(
          this.queryLicensesUrl,
          request,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  hasCloseToExpirationLicenses(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http
          .get<HasCloseToExpirationResponse>(
            this.hasCloseToExpirationLicensesUrl + '/' + tenantId,
            {
              headers: { Authorization: 'Bearer ' + token },
            }
          )
          .pipe(
            mergeMap((res) => {
              return of(res.has_close_to_expiration);
            })
          );
      })
    );
  }

  createDesktopLicenses(createRequest: CreateDesktopLicenseRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<CreateDesktopLicenseResponse>(
          this.addLicenseToTenantUrl,
          createRequest,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  collectCreatedLicenses(
    response: CreateDesktopLicenseResponse
  ): DesktopLicenseWithProduct[] {
    const created: DesktopLicenseWithProduct[] = [];
    if (response.new_primary_license) {
      created.push(response.new_primary_license);
    }
    if (response.add_on_licenses?.length) {
      created.push(...response.add_on_licenses);
    }
    return created;
  }

  updateLicenseWillRenew(req: UpdateDesktopLicenseWillRenewRequest[]) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<UpdateDesktopLicenseWillRenewResponse>(
          this.updateWillRenewUrl,
          req,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateLicense(licenseId: string, request: UpdateDesktopLicenseRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.put<LicenseWithProductResponse>(
          this.updateLicenseUrl + '/' + licenseId,
          request,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getPreemtiveExpirationDate(
    startDate: Date,
    variantId: string,
    parentLicenseId?: string
  ) {
    const request: PreemptiveExpirationRequest = {
      start_date: startDate,
      product_variant_id: variantId,
      parent_license_id: parentLicenseId,
    };

    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<PreemptiveExpirationResponse>(
          this.preemptiveExpirationUrl,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  getDurationOptions(licenseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<LicenseDurationOptionsResponse[]>(
          this.getDurationOptionsUrl + '/' + licenseId,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  getPriceLockLevels(licenseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ProductVariant[]>(
          this.getPriceLockLevelsUrl + '/' + licenseId,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  deleteDesktopLicense(id: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.delete<DeleteDesktopLicenseResponse>(
          this.deleteDesktopLicenseUrl + '/' + id,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      })
    );
  }

  transferLicenses(
    desktopLicenseIds: string[],
    uniqueId: string,
    isTransfer: boolean,
    syncRegistrationDatabase?: boolean
  ) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const body: Record<string, unknown> = {
          desktop_license_ids: desktopLicenseIds,
          unique_id: uniqueId,
          is_transfer: isTransfer,
        };
        if (syncRegistrationDatabase === true) {
          body['sync_registration_database'] = true;
        }
        return this.http.post<TransferLicensesResponse>(
          this.transferLicenseUrl,
          body,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  applyInitialUniqueIds(body: ApplyInitialUniqueIDsRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApplyInitialUniqueIDsResponse>(
          this.applyInitialUniqueIdsUrl,
          body,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  sortLicensesByRelationship(
    licenses: DesktopLicenseWithProduct[] | DesktopLicenseDetails[]
  ): DesktopLicenseWithProduct[] | DesktopLicenseDetails[] {
    let newList = [];

    // parent licenses should be licenses without a parent_desktop_license_id,
    // are not a site license, and do not require a parent license
    const parentLicenses = licenses.filter(
      (license) =>
        !license.parent_desktop_license_id &&
        !license.site_license &&
        !license.product.requires_parent
    );
    parentLicenses.sort((a, b) => {
      const startDateA = a.start_date ? new Date(a.start_date).getTime() : -1;
      const startDateB = b.start_date ? new Date(b.start_date).getTime() : -1;
      if (startDateA === startDateB) {
        return a.id < b.id ? -1 : 1;
      }
      return startDateA < startDateB ? -1 : 1;
    });

    for (const parent of parentLicenses) {
      console.log(
        'parent: ',
        parent.id,
        parent.product.title,
        parent.parent_desktop_license_id
      );
      newList.push(parent);
      const children = this.getChildLicenses(licenses, parent, []);
      // sort children so that site licenses of the same product variant are
      // at the top, then by add on, then by site license add ons
      children.sort((a, b) => {
        if (a.product_variant.id === parent.product_variant.id) {
          if (b.product_variant.id === parent.product_variant.id) {
            return a.id < b.id ? -1 : 1;
          }
          return -1;
        } else if (b.product_variant.id === parent.product_variant.id) {
          return 1;
        }
        if (!a.site_license && b.site_license) {
          return -1;
        } else if (a.site_license && !b.site_license) {
          return 1;
        }
        return a.id < b.id ? -1 : 1;
      });
      for (const child of children) {
        console.log(
          '  child: ',
          child.id,
          child.product.title,
          child.parent_desktop_license_id
        );
        // ensure that the child license is not alreaady in the new list
        if (newList.find((l) => l.id === child.id)) {
          console.log('  child already in new list, skipping');
          continue;
        }
        newList.push(child);
      }
    }

    // push any missing licenses to the new list
    for (const license of licenses) {
      if (!newList.find((l) => l.id === license.id)) {
        newList.push(license);
      }
    }

    return newList;
  }

  getPrice(license: DesktopLicenseWithProduct) {
    if (license.renewal && license.site_license) {
      return license.product_variant.site_renewal_price;
    } else if (license.renewal) {
      return license.product_variant.renewal_price;
    } else if (license.site_license) {
      return license.product_variant.site_price;
    } else {
      return license.product_variant.price;
    }
  }

  getType(license: DesktopLicenseWithProduct | DesktopLicenseDetails) {
    if (license.product.requires_parent) {
      if (license.site_license) {
        return 'Addon Additional';
      }
      return 'Addon';
    }

    if (license.site_license) {
      return 'Additional';
    }
    return 'Primary';
  }

  // getDurationString(months: number) {
  //   if (months % 12 === 0) {
  //     return months === 12 ? '1 year' : `${months / 12} years`;
  //   } else {
  //     return months === 1 ? '1 month' : `${months} months`;
  //   }
  // }

  licenseTypesMatch(
    a: DesktopLicenseWithProduct,
    b: DesktopLicenseWithProduct
  ) {
    return (
      a.product_variant.id === b.product_variant.id &&
      a.site_license === b.site_license &&
      a.renewal === b.renewal
    );
  }

  private getChildLicenses(
    licenses: DesktopLicenseWithProduct[],
    currentLicense: DesktopLicenseWithProduct,
    currentChildren: DesktopLicenseWithProduct[]
  ): DesktopLicenseWithProduct[] {
    // get children of current license
    // children should have a parent license id matching the target license,
    // or it is a site license of the same product variant
    const children = licenses.filter(
      (l) =>
        l.id !== currentLicense.id &&
        !currentChildren.find((c) => c.id === l.id) &&
        (l.parent_desktop_license_id === currentLicense.id ||
          (l.site_license &&
            l.product_variant.id === currentLicense.product_variant.id))
    );
    children.sort((a, b) => (a.product.code > b.product.code ? -1 : 1));

    for (const child of children) {
      currentChildren.push(child);
      const grandChildren = this.getChildLicenses(
        licenses,
        child,
        currentChildren
      );
      for (const grandChild of grandChildren) {
        if (currentChildren.find((c) => c.id === grandChild.id)) {
          continue;
        }
        currentChildren.push(grandChild);
      }
    }

    return currentChildren;
  }
}
